import type {
  ActivateInvitationRequest,
  RequestPasswordResetRequest,
  ResetPasswordRequest,
} from '@jq4race/shared';

import type { AuthRepository } from '../domain/ports/auth-repository';
import type { SessionFactory } from './ports/session-factory';
import { type ActivateMfaInput, activateMfaUseCase } from './use-cases/activate-mfa.use-case';
import { loginUseCase } from './use-cases/login.use-case';
import {
  type RequestEmailOtpInput,
  requestEmailOtpUseCase,
} from './use-cases/request-email-otp.use-case';
import {
  type VerifyEmailOtpInput,
  verifyEmailOtpUseCase,
} from './use-cases/verify-email-otp.use-case';
import { type VerifyMfaInput, verifyMfaUseCase } from './use-cases/verify-mfa.use-case';

interface AuthServicesDeps {
  authRepository: AuthRepository;
  createSession: SessionFactory;
}

export function createAuthServices(deps: AuthServicesDeps) {
  return {
    login: (input: Parameters<typeof loginUseCase>[0]) => loginUseCase(input, deps),
    activateInvitation: (input: ActivateInvitationRequest) =>
      deps.authRepository.activateInvitation(input),
    requestPasswordReset: (email: RequestPasswordResetRequest['email']) =>
      deps.authRepository.requestPasswordReset(email),
    resetPassword: (input: ResetPasswordRequest) => deps.authRepository.resetPassword(input),
    setupMfa: (mfaToken: string) => deps.authRepository.setupMfa({ mfaToken }),
    activateMfa: (input: ActivateMfaInput) => activateMfaUseCase(input, deps),
    verifyMfa: (input: VerifyMfaInput) => verifyMfaUseCase(input, deps),
    requestEmailOtp: (input: RequestEmailOtpInput) => requestEmailOtpUseCase(input, deps),
    verifyEmailOtp: (input: VerifyEmailOtpInput) => verifyEmailOtpUseCase(input, deps),
  };
}

export type AuthServices = ReturnType<typeof createAuthServices>;
